// 非編集表示・コメント本文が共有する、Connect Note 互換 callout（`:::note info` … `:::`）の描画。
//
// - 開始フェンス行・終了フェンス行は表示しない。タイトル（開始行の type より後ろ）は
//   callout の先頭へ太字の段落として置く。
// - **コンテナへ `data-md-line` を付けない。** 付けると行要素を引くセレクタがコンテナを選び、
//   範囲選択の矩形収集でコンテナと各行が二重に集まる（表の wrapper と同じ理由）。
// - タイトル要素の `data-md-line` は開始フェンス行の index。タイトルが無い callout では
//   タイトル要素そのものを作らない（判定は raw 範囲の有無ではなく表示文字が 1 文字以上あるか）。
// - 本文行は callout の外とまったく同じ作り方（MarkdownLineView）にする。callout は囲むだけである。
// - 本文 0 行・タイトル無しの callout は `<br>` だけの行を 1 つ置いて 1 行分の高さを確保する。
//   この行にも開始フェンス行の `data-md-line` を付ける（付けないと、この callout の中に
//   キャレットを表現できる要素が 1 つも無くなる）。
// - 計測ビルダー（buildMarkdownTextContent の appendMeasuredCallout）と同じ構造を作る
//   （構造一致はパリティテストで担保）。

import {
  markdownCalloutHasTitle,
  type MarkdownCallout,
  type MarkdownSourceMap,
  type TextInlineStyleRange,
} from "@refinement-canvas/domain";
import {
  markdownCalloutBodyPreviousKinds,
  markdownCalloutClassName,
  markdownCalloutTitleSpans,
  MARKDOWN_CALLOUT_TITLE_CLASS,
} from "../../../lib/markdown-text";
import { MarkdownLineView, type MarkdownInlineRenderer } from "./markdown-line-view";

interface Props {
  readonly sourceMap: MarkdownSourceMap;
  readonly callout: MarkdownCallout;
  /** 文字範囲だけへ適用する部分書式（正規形の run 配列）。 */
  readonly inlineStyles?: readonly TextInlineStyleRange[] | undefined;
  readonly renderInline: MarkdownInlineRenderer;
  /** 非編集・非ロックのキャンバスオブジェクトだけが渡す task 切り替えハンドラ。 */
  readonly onToggleTask?: ((checkMarkOffset: number) => void) | undefined;
}

export function MarkdownCalloutView({
  sourceMap,
  callout,
  inlineStyles,
  renderInline,
  onToggleTask,
}: Props): JSX.Element {
  const hasTitle = markdownCalloutHasTitle(sourceMap.text, callout);
  const previousKinds = markdownCalloutBodyPreviousKinds(sourceMap, callout);
  return (
    <div className={markdownCalloutClassName(callout.type)}>
      {hasTitle ? (
        <div className={MARKDOWN_CALLOUT_TITLE_CLASS} data-md-line={callout.openLineIndex}>
          {/* タイトルの太字は「行の基底の太字」（CSS の font-weight: 700）で、部分書式 run の
              bold: false は子 span 側の font-weight: 400 が勝って打ち消す。参照元で
              タイトルが **タイトル** という通常の Markdown 太字であることと同じ挙動になる。 */}
          {renderInline({
            headingLine: false,
            spans: markdownCalloutTitleSpans(sourceMap.text, callout, inlineStyles),
          })}
        </div>
      ) : null}
      {callout.body.map((body, index) => (
        <MarkdownLineView
          key={body.lineIndex}
          inlineStyles={inlineStyles}
          lineIndex={body.lineIndex}
          previousKind={previousKinds[index]}
          renderInline={renderInline}
          sourceMap={sourceMap}
          onToggleTask={onToggleTask}
        />
      ))}
      {!hasTitle && callout.body.length === 0 ? (
        <div data-md-line={callout.openLineIndex}>
          <br />
        </div>
      ) : null}
    </div>
  );
}
